import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { todayIso } from '@platform/core/lib/format';
import { routes } from '@platform/core/lib/routes';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { catalogLabel, navGroup } from '@platform/navigation';
import { AreaModuleCards } from './AreaModuleCards';
import { AreaRecordList } from './AreaRecordList';
import { useAreaStats } from './useAreaStats';
import '../bi/bi.css';
import '../work/work.css';
import './area.css';

/**
 * Огляд напряму (прототип v4 page "area"; шапка меню групи «… · відкрити огляд напряму →»): «Огляд напряму», назва групи, «N модулів ·
 * M записів · K відкрито», показники (відкрито, прострочено, термін за 7 днів, призначені мені, виконано · 30 днів), картки модулів,
 * «Прострочені й на цей тиждень», «Призначені мені» й «Нещодавно оновлені».
 */
export function AreaPage() {
  const { t } = useTranslation();
  const label = useLabel();
  const { group = '' } = useParams();
  const { ws } = useWorkspaceParams();
  const { modules, stats } = useAreaStats(ws, group);
  const def = navGroup(group);
  if (!def) return <Navigate to={routes.home()} replace />;
  const today = todayIso();
  const kpi = (key: string, value: number | undefined, tone: string) => (
    <div key={key} className="bi-kpi"><span className="bi-kpi__label">{t(`areaPage.kpi.${key}`)}</span><span className="bi-kpi__value" style={{ color: tone }}>{value ?? '—'}</span></div>
  );
  return (
    <div className="ar-page bi-page">
      <div className="ar-head">
        <span className="ar-eyebrow">{t('areaPage.eyebrow')}</span>
        <h1>{label(catalogLabel(def))}</h1>
        <div className="muted">{[t('shell.modulesCount', { count: modules.length }), stats && t('areaPage.records', { count: stats.records }), stats && t('areaPage.open', { count: stats.open })].filter(Boolean).join(' · ')}</div>
      </div>
      <div className="ar-kpis">
        {kpi('open', stats?.open, 'var(--bi-blue)')}
        {kpi('overdue', stats?.overdue, stats?.overdue ? 'var(--bi-orange)' : 'var(--muted)')}
        {kpi('soon', stats?.soon, 'var(--ink)')}
        {kpi('mine', stats?.mine, 'var(--bi-purple)')}
        {kpi('done', stats?.done30, 'var(--bi-green)')}
      </div>
      {stats && <AreaModuleCards cards={stats.modules} />}
      {stats && (
        <div className="ar-columns">
          <AreaRecordList title={t('areaPage.overdueSoon')} ws={ws} records={stats.overdueAndSoon} today={today} />
          <AreaRecordList title={t('areaPage.mine')} ws={ws} records={stats.assigned} today={today} />
          <AreaRecordList title={t('areaPage.recent')} ws={ws} records={stats.recent} today={today} />
        </div>
      )}
      {!stats && <div className="bi-loading">{t('homeViews.loading')}</div>}
    </div>
  );
}
